export interface ProjectMetric {
  label: string;
  value: string;
  subtext?: string;
}

export interface WorkShowcaseProject {
  id: string;
  slug: string;
  name: string;
  title: string;
  themeColor: string;
  accentHex: string;
  client: string;
  year: string;
  status: string;
  heroStatement: string;
  summary: string;
  challenge: string;
  services: string[];
  filterTags: string[];
  location: string;
  metrics: ProjectMetric[];
  screenshots: {
    src: string;
    alt: string;
    caption?: string;
  }[];
}

export const FILTER_OPTIONS = [
  { id: "all", label: "ALL" },
  { id: "ai-workflows", label: "AI WORKFLOWS" },
  { id: "multi-agent", label: "MULTI-AGENT SWARMS" },
  { id: "enterprise-rag", label: "ENTERPRISE RAG" },
  { id: "full-stack", label: "FULL-STACK ARCHITECTURE" },
  { id: "fintech", label: "FINTECH & COMPLIANCE" },
  { id: "data-pipelines", label: "DATA PIPELINES" },
];

export const ALL_PROJECTS: WorkShowcaseProject[] = [
  {
    id: "ai-customer-support",
    slug: "ai-customer-support",
    name: "TechSupport AI",
    title: "Autonomous Customer Support Assistant with Policy Guardrails",
    themeColor: "bg-[#f24e1e]",
    accentHex: "#f24e1e",
    client: "TechSupport Solutions (Enterprise)",
    year: "2023",
    status: "Active in Production",
    heroStatement:
      "Transforming high-volume Tier-1 customer support from inconsistent outsourced queues into an autonomous, zero-hallucination AI concierge.",
    summary:
      "Enterprise AI customer support assistant with deterministic policy guardrails, automated CRM integration, and instant documentation grounding.",
    challenge:
      "Agency-delivered legacy bot had a 12% policy violation rate and hallucinated unsupported refund rules, risking brand reputation and customer trust.",
    services: ["AI WORKFLOWS", "FULL-STACK WEB", "RAG PIPELINES", "GUARDRAILS"],
    filterTags: ["ai-workflows", "enterprise-rag", "full-stack"],
    location: "US",
    metrics: [
      { label: "DEFLECTION RATE", value: "70%", subtext: "Tier-1 tickets resolved without human intervention" },
      { label: "POLICY ACCURACY", value: "100%", subtext: "Deterministic guardrail policy enforcement" },
      { label: "FIRST RESPONSE", value: "<1.2s", subtext: "Real-time streaming resolution across 50k DAU" }
    ],
    screenshots: [
      { src: "/images/work/support-hero.jpg", alt: "Support Assistant Live Concierge", caption: "Live Support Concierge with citation badges & customer profile grounding" },
      { src: "/images/work/support-guardrail.jpg", alt: "Policy Guardrail Inspector", caption: "Real-time policy enforcement engine validating response rules before dispatch" },
      { src: "/images/work/support-analytics.jpg", alt: "Ticket Deflection Analytics", caption: "Telemetry dashboard tracking deflection velocity and cost savings" }
    ]
  },
  {
    id: "agentic-workflow-orchestration",
    slug: "agentic-workflow-orchestration",
    name: "FinSwarm KYC",
    title: "Autonomous Multi-Agent Swarm for FinTech Identity Onboarding",
    themeColor: "bg-[#1e293b]",
    accentHex: "#38bdf8",
    client: "Series C FinTech Platform",
    year: "2024",
    status: "Active in Production",
    heroStatement:
      "Replacing a 3-day manual compliance review pipeline with a coordinated swarm of autonomous LangGraph agents operating in under 5 minutes.",
    summary:
      "Autonomous multi-agent onboarding swarm orchestrating identity verification, facial liveness checks, and AML compliance in under 5 minutes.",
    challenge:
      "Manual compliance reviews created a 3-day bottleneck, causing a 42% drop-off in high-intent merchant signups during peak growth.",
    services: ["MULTI-AGENT SWARMS", "LANGGRAPH", "FINTECH COMPLIANCE", "FASTAPI"],
    filterTags: ["multi-agent", "ai-workflows", "fintech"],
    location: "US / UK",
    metrics: [
      { label: "VERIFICATION TIME", value: "3 Days → 4.2 Min", subtext: "Full biometric and AML verification cycle" },
      { label: "CONVERSION GAIN", value: "+38%", subtext: "Immediate signup completion velocity" },
      { label: "FALSE POSITIVES", value: "<0.01%", subtext: "Cross-checked against global watchlists" }
    ],
    screenshots: [
      { src: "/images/work/finswarm-hero.jpg", alt: "FinSwarm KYC Identity Portal", caption: "End-user identity verification and biometric capture interface" },
      { src: "/images/work/kyc-portal.jpg", alt: "Biometric & Document Verification", caption: "Autonomous OCR extraction and passport biometric match engine" },
      { src: "/images/work/finswarm-compliance.jpg", alt: "Multi-Agent Compliance Analytics", caption: "Supervisor agent telemetry monitoring multi-agent consensus decisions" }
    ]
  },
  {
    id: "enterprise-rag-system",
    slug: "enterprise-rag-system",
    name: "LexisRAG Legal",
    title: "Deterministic Legal Intelligence & Hybrid Vector Knowledge Base",
    themeColor: "bg-[#064e3b]",
    accentHex: "#34d399",
    client: "Global Legal Advisory Group",
    year: "2024",
    status: "Active in Production",
    heroStatement:
      "Indexing 100,000+ complex multi-jurisdiction contracts with sentence-level citation attribution, hybrid BM25/Dense search, and zero hallucinations.",
    summary:
      "Enterprise legal document intelligence platform indexing 100,000+ contracts with citation grounding, semantic search, and zero hallucinations.",
    challenge:
      "Paralegals spent 15+ hours per diligence audit manually searching clauses across thousands of non-standardized PDF agreements.",
    services: ["VECTOR SEARCH", "HYBRID RAG", "DOCUMENT PARSING", "NEXT.JS 16"],
    filterTags: ["enterprise-rag", "full-stack"],
    location: "EU",
    metrics: [
      { label: "AUDIT TIME", value: "15h → 18min", subtext: "Comprehensive clause discovery per contract set" },
      { label: "ATTRIBUTION", value: "100%", subtext: "Direct PDF bounding-box citation grounding" },
      { label: "CONTRACT CORPUS", value: "100k+", subtext: "Indexed in scalable pgvector cluster" }
    ],
    screenshots: [
      { src: "/images/work/lexisrag-hero.jpg", alt: "LexisRAG Enterprise Knowledge Base", caption: "Legal knowledge base with multi-repository indexing and semantic filter tree" },
      { src: "/images/work/lexisrag-clause.jpg", alt: "Semantic Query Attribution", caption: "Clause comparison workbench with direct source document highlighting" },
      { src: "/images/work/lexisrag-policy.jpg", alt: "Enterprise Tier Configuration", caption: "Role-based access control and air-gapped tenant isolation interface" }
    ]
  },
  {
    id: "llm-data-pipeline",
    slug: "llm-data-pipeline",
    name: "DataPulse ETL",
    title: "High-Throughput Financial Data Extraction & Validation Engine",
    themeColor: "bg-[#1e1b4b]",
    accentHex: "#818cf8",
    client: "Institutional Quantitative Fund",
    year: "2024",
    status: "Active in Production",
    heroStatement:
      "Transforming unstructured SEC 10-K and 10-Q filings into deterministic P&L schemas with 100% Pydantic validation and microsecond database write queues.",
    summary:
      "Automated financial extraction & analytics engine turning unstructured 10-K filings into deterministic P&L schemas with 100% Pydantic validation.",
    challenge:
      "Financial tables varied wildly across 500+ public companies, causing brittle legacy regex extractors to corrupt fundamental analyst models.",
    services: ["DATA PIPELINES", "PYDANTIC", "FASTAPI", "POSTGRESQL"],
    filterTags: ["data-pipelines", "ai-workflows", "fintech"],
    location: "US",
    metrics: [
      { label: "SCHEMA ACCURACY", value: "99.98%", subtext: "Pydantic-validated JSON extraction schemas" },
      { label: "PROCESSING SPEED", value: "12s / 10-K", subtext: "200-page complex filing extraction" },
      { label: "DAILY VOLUME", value: "50k+ Docs", subtext: "High-throughput Celery/FastAPI worker pool" }
    ],
    screenshots: [
      { src: "/images/work/datapulse-hero.jpg", alt: "Financial Extraction Workbench", caption: "Real-time SEC filing parser and balance sheet reconciler" },
      { src: "/images/work/datapulse-validator.jpg", alt: "JSON Schema Validator", caption: "Strict Pydantic rule enforcement and schema mismatch quarantine" },
      { src: "/images/work/datapulse-waterfall.jpg", alt: "P&L Variance Analytics", caption: "Historical fundamental telemetry and variance distribution" }
    ]
  },
  {
    id: "defi-trading-platform",
    slug: "defi-trading-platform",
    name: "Aegis DeFi",
    title: "Institutional Decentralized Yield Aggregator & Smart Order Router",
    themeColor: "bg-[#0e3a47]",
    accentHex: "#22d3ee",
    client: "Aegis Protocol (Web3)",
    year: "2023",
    status: "Active in Production",
    heroStatement:
      "Architecting a high-throughput liquidity router and non-custodial yield engine processing $2M+ in daily automated smart contract rebalancing.",
    summary:
      "Decentralized finance trading engine processing $2M+ daily volume with AI-driven yield optimization, smart order routing, and zero downtime.",
    challenge:
      "Fragmented liquidity pools caused high slippage on institutional trades, while fluctuating gas spikes eroded rebalancing yields.",
    services: ["SMART CONTRACTS", "SOLIDITY", "REACT 19", "WEB3.JS"],
    filterTags: ["full-stack", "fintech"],
    location: "GLOBAL",
    metrics: [
      { label: "DAILY VOLUME", value: "$2M+", subtext: "Automated routing across 12 DEX liquidity pools" },
      { label: "SLIPPAGE REDUCTION", value: "-64%", subtext: "Split-order dynamic routing algorithm" },
      { label: "UPTIME", value: "100%", subtext: "Zero smart contract security incidents since launch" }
    ],
    screenshots: [
      { src: "/images/work/aegis-hero.jpg", alt: "Aegis DeFi Terminal", caption: "Institutional trading terminal with real-time liquidity depth and order books" },
      { src: "/images/work/aegis-liquidity.jpg", alt: "Multi-DEX Liquidity Analytics", caption: "Multi-pool yield aggregator with gas-optimized smart contract routing" },
      { src: "/images/work/aegis-vaults.jpg", alt: "Yield Pool Matrix", caption: "Institutional yield vaults with automated risk-weighted allocation" }
    ]
  },
  {
    id: "ai-content-generator",
    slug: "ai-content-generator",
    name: "Synapse AI",
    title: "Enterprise Brand Governance & Multi-Modal Content Engine",
    themeColor: "bg-[#3b0764]",
    accentHex: "#c084fc",
    client: "Global SaaS Enterprise",
    year: "2024",
    status: "Active in Production",
    heroStatement:
      "Enforcing strict brand voice, tone compliance, and multi-channel synchronization across 40+ global product marketing teams.",
    summary:
      "Enterprise AI content & brand consistency pipeline with automated style governance, tone alignment filters, and multi-channel publishing.",
    challenge:
      "Global marketing teams were publishing off-brand messaging with inconsistent terminology and unvetted product claims across regional portals.",
    services: ["GENAI", "NLP", "STYLE GOVERNANCE", "TYPESCRIPT"],
    filterTags: ["ai-workflows", "full-stack"],
    location: "US",
    metrics: [
      { label: "BRAND COMPLIANCE", value: "98.5%", subtext: "Automated tone and terminology scoring" },
      { label: "TIME-TO-PUBLISH", value: "4 Days → 2h", subtext: "End-to-end multi-channel review cycle" },
      { label: "ACTIVE MARKETERS", value: "250+", subtext: "Internal enterprise workspace adoption" }
    ],
    screenshots: [
      { src: "/images/work/synapse-hero.jpg", alt: "Synapse AI Workspace", caption: "Multi-channel content generation workspace with live brand tone scoring" },
      { src: "/images/work/synapse-tone.jpg", alt: "Brand Voice Inspector", caption: "Automated style compliance rules quarantine and terminology corrector" },
      { src: "/images/work/synapse-velocity.jpg", alt: "Content Velocity Telemetry", caption: "Enterprise marketing velocity and regional engagement dashboard" }
    ]
  }
];
